import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import Paginacao from '../paginacao/Paginacao';
import { notify } from '../../shared/feedback/notify';
import ModalSync from './ModalSync';
import TabelaSistema, { ColunaTabela } from './TabelaSistema';
import { classeStatus, duracaoLegivel, formatarDataHoraCurta, rotuloStatus } from './statusBadge';
import { JobResumo, SyncJobStatus } from './types';

const PAGE_SIZE = 15;

export interface HistoricoFiltros {
  tipo?: string;
  status?: SyncJobStatus;
}

/** Uma opção do filtro de tipo — cada painel declara as suas. */
export interface OpcaoTipo {
  valor: string;
  rotulo: string;
}

interface Props {
  onVerDetalhe: (jobId: string) => void;
  algumRodando: boolean;
  /** Busca paginada do painel dono da tela. */
  fetchJobs: (params: { tipo?: string; status?: string; page?: number; pageSize?: number }) => Promise<{
    total: number;
    jobs: JobResumo[];
  }>;
  /** Prefixo da chave de cache, para os painéis não compartilharem entradas. */
  queryKeyBase: string;
  /** Rótulo curto por tipo, usado na coluna da tabela. */
  rotulosTipo: Record<string, string>;
  /** Opções do filtro de tipo. */
  opcoesTipo: OpcaoTipo[];
  /** Cabeçalho da primeira coluna (o banco chama de "Bloco"; a FIPE, de "Tipo"). */
  tituloColunaTipo?: string;
}

/**
 * Filtros do histórico em modal, no padrão do projeto: estado local, aplicação em bloco
 * ("Aplicar Filtros" gera UMA requisição), "Cancelar" desfaz de verdade, aplicar volta
 * para a página 1 e o modal RECEBE o que está aplicado (`filtrosAplicados`) — sem isso
 * ele reabriria em branco e um "Aplicar" limparia tudo em silêncio (BUG-V43).
 * Montagem condicional pelo chamador; nada de prop `isOpen`.
 */
const FiltrosModal: React.FC<{
  filtrosAplicados: HistoricoFiltros;
  opcoesTipo: OpcaoTipo[];
  rotuloCampoTipo: string;
  onAplicar: (f: HistoricoFiltros) => void;
  onClose: () => void;
}> = ({ filtrosAplicados, opcoesTipo, rotuloCampoTipo, onAplicar, onClose }) => {
  const [tipo, setTipo] = useState<string>(filtrosAplicados.tipo ?? '');
  const [status, setStatus] = useState<string>(filtrosAplicados.status ?? '');

  return (
    <ModalSync
      titulo="Filtros do histórico"
      subtitulo="Escolha o tipo e a situação das execuções"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="filtro-reset-btn" onClick={() => onAplicar({})}>
            Limpar filtros
          </button>
          <button type="button" className="filtro-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="filtro-btn-primary"
            onClick={() =>
              onAplicar({
                tipo: tipo || undefined,
                status: (status || undefined) as SyncJobStatus | undefined,
              })
            }
          >
            Aplicar Filtros
          </button>
        </>
      }
    >
      <div className="filtro-section">
        <h3 className="filtro-section-title">Execuções</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label>{rotuloCampoTipo}</label>
            <div className="select-wrapper">
              <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="">Todos</option>
                {opcoesTipo.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="filtro-field">
            <label>Situação</label>
            <div className="select-wrapper">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todas</option>
                <option value="RUNNING">Em execução</option>
                <option value="SUCESSO">Sucesso</option>
                <option value="FALHA_PARCIAL">Falha parcial</option>
                <option value="RECUPERADO">Recuperado</option>
                <option value="FALHA">Falha</option>
                <option value="CANCELADO">Cancelado</option>
                <option value="ABANDONADO">Abandonado</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </ModalSync>
  );
};

const detalheDoJob = (j: JobResumo): string =>
  j.etapasComProblema.length > 0
    ? `${j.etapasComProblema.length} etapa(s) com problema: ${j.etapasComProblema.join(', ')}`
    : j.mensagem ?? `${j.totalEtapas} etapa(s)`;

/** Histórico paginado de todas as execuções de um painel de sincronização. */
const HistoricoSection: React.FC<Props> = ({
  onVerDetalhe,
  algumRodando,
  fetchJobs,
  queryKeyBase,
  rotulosTipo,
  opcoesTipo,
  tituloColunaTipo = 'Tipo',
}) => {
  const [filtros, setFiltros] = useState<HistoricoFiltros>({});
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [page, setPage] = useState(1);

  const { data, error, isError, isLoading } = useQuery({
    queryKey: [queryKeyBase, 'jobs', filtros, page],
    queryFn: () => fetchJobs({ ...filtros, page, pageSize: PAGE_SIZE }),
    refetchInterval: algumRodando ? 15_000 : false,
  });

  useEffect(() => {
    if (isError) {
      notify.errorFrom(error, 'Não foi possível carregar o histórico de sincronizações. Recarregue a página; se persistir, acione a TI.', {
        toastId: `${queryKeyBase}-historico`,
      });
    }
  }, [isError, error, queryKeyBase]);

  const colunas = useMemo<ColunaTabela<JobResumo>[]>(
    () => [
      { titulo: tituloColunaTipo, largura: 130, texto: (j) => rotulosTipo[j.tipo] ?? j.tipo },
      {
        titulo: 'Situação',
        largura: 130,
        texto: (j) => rotuloStatus(j.status),
        render: (j) => <span className={classeStatus(j.status)}>{rotuloStatus(j.status)}</span>,
      },
      { titulo: 'Início', largura: 150, texto: (j) => formatarDataHoraCurta(j.startedAt) },
      {
        titulo: 'Duração',
        largura: 110,
        texto: (j) => (j.finishedAt ? duracaoLegivel(j.startedAt, j.finishedAt) : 'em andamento'),
      },
      { titulo: 'Detalhes', largura: 280, texto: detalheDoJob },
    ],
    [rotulosTipo, tituloColunaTipo],
  );

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div className="admin-card-title" style={{ marginBottom: 0 }}>
          Histórico de execuções
        </div>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setFiltrosAbertos(true)}>
          Filtros
        </button>
      </div>

      <p className="admin-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        {data ? `${data.total} execução(ões) registrada(s). ` : ''}Clique numa linha para ver as etapas.
      </p>

      {isError && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar o histórico. A lista abaixo pode estar incompleta.
        </div>
      )}

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <TabelaSistema
          colunas={colunas}
          itens={data?.jobs ?? []}
          chave={(j) => j.id}
          carregando={isLoading}
          vazio={isError ? 'O histórico não pôde ser carregado — veja o aviso acima.' : 'Nenhuma execução para os filtros aplicados.'}
          onClickLinha={(j) => onVerDetalhe(j.id)}
        />

        {totalPaginas > 1 && <Paginacao paginaAtual={page} totalPaginas={totalPaginas} onMudarPagina={setPage} />}
      </div>

      {filtrosAbertos && (
        <FiltrosModal
          filtrosAplicados={filtros}
          opcoesTipo={opcoesTipo}
          rotuloCampoTipo={tituloColunaTipo}
          onClose={() => setFiltrosAbertos(false)}
          onAplicar={(f) => {
            setFiltros(f);
            setPage(1);
            setFiltrosAbertos(false);
          }}
        />
      )}
    </>
  );
};

export default HistoricoSection;
